import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createVNode as _createVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "title"
}
const _hoisted_2 = { class: "content" }
const _hoisted_3 = { class: "tabs" }
const _hoisted_4 = { class: "header" }
const _hoisted_5 = { class: "search" }
const _hoisted_6 = { class: "icon" }
const _hoisted_7 = { class: "actions" }
const _hoisted_8 = { class: "list" }
const _hoisted_9 = {
  key: 0,
  class: "flex items-center justify-center"
}
const _hoisted_10 = {
  key: 0,
  class: "title"
}
const _hoisted_11 = { class: "flex flex-row flex-1 justify-between" }
const _hoisted_12 = { class: "close" }

import { computed, ref, onMounted, onUnmounted, nextTick } from "vue";
import { useI18n } from "vue-i18n";

import { toast } from "@/common/utils/toast";
import { getDifference } from "@/common/utils/dates";

import {
  analysisCreate,
  analysisCopy,
  analysisDelete,
  analysisList,
} from "@/common/api/rest/analysis/analysis";

import type { AnalysisListType } from "@/common/types/analysis";
import type { EvaluationType } from "@/common/types/evaluation";

import { analysisHome } from "@/components";
import {
  baseConfirm,
  baseLoading,
  baseInput,
  baseTooltip,
  svgIcon,
  cardAnalysis,
} from "@/common/components";


export default /*@__PURE__*/_defineComponent({
  __name: 'analysis',
  props: {
    data: {},
    width: { default: "100%" },
    showTitle: { type: Boolean, default: true },
    isEdit: { type: Boolean, default: true }
  },
  emits: ["update-data", "selected-code"],
  setup(__props: any, { emit: __emit }) {

const { t } = useI18n();

const emit = __emit;

const props = __props;

const observerRef = ref();
const intersectionObserver = ref<IntersectionObserver | null>(null);
const isObserverVisible = ref(false);

const analysis = ref<AnalysisListType[]>([]);
const selected = ref<AnalysisListType | null>(null);
const tab = ref<string>("PENDING");

const limit = ref<number>(10);
const page = ref<number>(0);
const pages = ref<number>(0);
const searchInput = ref<string>("");
const count = ref<number>(0);
const total = ref<number>(0);

const loading = ref(false);
const hasMore = ref(true);
const analysisEdit = ref(false);
const showAddConfirm = ref(false);
const showDelConfirm = ref(false);
const showCopyConfirm = ref(false);

function resetPagination() {
  analysis.value = [];
  limit.value = 10;
  page.value = 0;
  searchInput.value = "";
  hasMore.value = true;
}

function fetchData() {
  if (!props.data) return;

  loading.value = true;

  analysisList(props.data.account_id, {
    evaluation_id: props.data.evaluation_id,
    limit: limit.value,
    page: page.value,
    search: searchInput.value,
    status: tab.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        analysis.value = Array.from(new Set([...analysis.value, ...data.list]));
        count.value = data.infos.count;
        total.value = data.infos.total;
        pages.value = data.infos.pages;
        if (data.list.length === 0) {
          hasMore.value = false;
        }
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      loading.value = false;

      // O IntersectionObserver não dispara de novo se o elemento
      // permanece visível. Então, após cada load, reavaliamos manualmente.
      nextTick(() => {
        if (
          isObserverVisible.value &&
          hasMore.value &&
          page.value + 1 < pages.value
        ) {
          tryLoadMore();
        }
      });
    });
}

function tryLoadMore() {
  if (!isObserverVisible.value || !hasMore.value || loading.value) return;

  const nextPage = page.value + 1;

  if (nextPage >= pages.value) {
    hasMore.value = false;
    return;
  }

  page.value = nextPage;
  fetchData();
}

function update() {
  emit("update-data");
}

function add() {
  showAddConfirm.value = true;
}

function addCancel() {
  showAddConfirm.value = false;
}

function addConfirm() {
  if (!props.data) return;

  showAddConfirm.value = false;

  analysisCreate(props.data.account_id, props.data.evaluation_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        toast({ message: message, type: "success" });
        resetPagination();
        fetchData();
        update();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function edit(data: AnalysisListType) {
  selected.value = data;
  analysisEdit.value = true;
  emit("selected-code", data.code);
}

function editCancel() {
  selected.value = null;
  analysisEdit.value = false;
  fetchData();
}

function del(data: AnalysisListType) {
  selected.value = data;
  showDelConfirm.value = true;
}

function delCancel() {
  showDelConfirm.value = false;
  selected.value = null;
}

function delConfirm() {
  if (!selected.value) return;

  showDelConfirm.value = false;

  analysisDelete(selected.value.account_id, selected.value.analysis_id)
    .then((response) => {
      const { success, message } = response;
      if (success) {
        resetPagination();
        delCancel();
        fetchData();
        update();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function clearSearch() {
  if (searchInput.value?.trim()?.length > 0) return;

  searchInput.value = "";
  fetchData();
}

function cleanList() {
  hasMore.value = true;
  analysis.value = [];
  page.value = 0;
}

function changeTab(value: string) {
  tab.value = value;
  page.value = 0;
  analysis.value = [];
  hasMore.value = true;
  fetchData();
}

function copy(data: AnalysisListType) {
  selected.value = data;
  showCopyConfirm.value = true;
}

function copyCancel() {
  selected.value = null;
  showCopyConfirm.value = false;
}

async function copyConfirm() {
  if (!selected.value) return;

  analysisCopy(selected.value.account_id, selected.value.analysis_id)
    .then((response) => {
      const { success, message } = response;
      if (success) {
        toast({ message: message, type: "success" });
        resetPagination();
        fetchData();
        copyCancel();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

onUnmounted(() => {
  if (intersectionObserver.value) {
    intersectionObserver.value.disconnect();
  }
});

onMounted(() => {
  intersectionObserver.value = new IntersectionObserver(
    (entries) => {
      isObserverVisible.value = entries[0].isIntersecting;

      if (entries[0].isIntersecting) {
        tryLoadMore();
      }
    },
    {
      rootMargin: "200px",
    },
  );

  if (observerRef.value) {
    intersectionObserver.value.observe(observerRef.value);
  }

  fetchData();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", {
    class: _normalizeClass(["tab-component", { width: __props.width }])
  }, [
    (!analysisEdit.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          (__props.showTitle)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, _toDisplayString(_unref(t)("common.labels.analysis")), 1))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("div", {
                class: _normalizeClass(["tab", {
              active: tab.value === 'PENDING',
            }]),
                onClick: _cache[0] || (_cache[0] = ($event: any) => (changeTab('PENDING')))
              }, " PENDENTES ", 2),
              _createElementVNode("div", {
                class: _normalizeClass(["tab", {
              active: tab.value === 'PROGRESS',
            }]),
                onClick: _cache[1] || (_cache[1] = ($event: any) => (changeTab('PROGRESS')))
              }, " INICIADAS ", 2),
              _createElementVNode("div", {
                class: _normalizeClass(["tab", {
              active: tab.value === 'CONCLUDED',
            }]),
                onClick: _cache[2] || (_cache[2] = ($event: any) => (changeTab('CONCLUDED')))
              }, " FINALIZADAS ", 2)
            ]),
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createVNode(_unref(baseInput), {
                  modelValue: searchInput.value,
                  "onUpdate:modelValue": [
                    _cache[4] || (_cache[4] = ($event: any) => ((searchInput).value = $event)),
                    _cache[5] || (_cache[5] = ($event: any) => (clearSearch()))
                  ],
                  background: "#fff",
                  color: "#000",
                  placeholder: _unref(t)('common.labels.search'),
                  "onKey:enter": _cache[6] || (_cache[6] = ($event: any) => {
                cleanList();
                fetchData();
              })
                }, {
                  sufix: _withCtx(() => [
                    _createElementVNode("div", _hoisted_6, [
                      _createVNode(_unref(svgIcon), {
                        icon: "search",
                        size: "1.75rem",
                        onClick: _cache[3] || (_cache[3] = ($event: any) => {
                      cleanList();
                      fetchData();
                    })
                      })
                    ])
                  ]),
                  _: 1
                }, 8, ["modelValue", "placeholder"])
              ]),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("button", {
                  class: "btn btn-md btn-primary",
                  onClick: _cache[7] || (_cache[7] = ($event: any) => (add()))
                }, [
                  _createVNode(_unref(svgIcon), {
                    icon: "add",
                    size: "1.1rem"
                  }),
                  _createElementVNode("span", null, _toDisplayString(_unref(t)("common.labels.add")?.toUpperCase()), 1)
                ])
              ])
            ]),
            _createElementVNode("div", _hoisted_8, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(analysis.value, (item) => {
                return (_openBlock(), _createBlock(_unref(cardAnalysis), {
                  key: item.analysis_id,
                  data: item,
                  onEdit: ($event: any) => (edit(item)),
                  onCopy: ($event: any) => (copy(item)),
                  onDelete: ($event: any) => (del(item))
                }, null, 8, ["data", "onEdit", "onCopy", "onDelete"]))
              }), 128)),
              (loading.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                    _createVNode(_unref(baseLoading))
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", {
                ref_key: "observerRef",
                ref: observerRef,
                class: "observer"
              }, null, 512)
            ])
          ])
        ], 64))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          (__props.showTitle)
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                _createElementVNode("div", _hoisted_11, [
                  _createElementVNode("span", null, _toDisplayString(_unref(t)("common.labels.analyse")) + " - " + _toDisplayString(selected.value?.code), 1),
                  _createVNode(_unref(baseTooltip), null, {
                    trigger: _withCtx(() => [
                      _createElementVNode("button", {
                        class: "btn btn-back btn-sm",
                        onClick: _cache[8] || (_cache[8] = ($event: any) => (editCancel()))
                      }, [
                        _createVNode(_unref(svgIcon), {
                          icon: "close",
                          size: "1.25rem"
                        })
                      ])
                    ]),
                    tooltip: _withCtx(() => [
                      _createElementVNode("div", _hoisted_12, _toDisplayString(_unref(t)("common.labels.close")), 1)
                    ]),
                    _: 1
                  })
                ])
              ]))
            : _createCommentVNode("", true),
          (selected.value)
            ? (_openBlock(), _createBlock(_unref(analysisHome), {
                key: 1,
                data: selected.value,
                "evaluation-code": __props.data?.code ?? '',
                company: __props.data?.company ?? null,
                "address-id": __props.data?.address_id ?? null,
                "is-edit": __props.isEdit,
                onUpdateData: _cache[9] || (_cache[9] = ($event: any) => (fetchData()))
              }, null, 8, ["data", "evaluation-code", "company", "address-id", "is-edit"]))
            : _createCommentVNode("", true)
        ], 64)),
    _createVNode(_unref(baseConfirm), {
      show: showAddConfirm.value,
      "max-width": "400px",
      title: _unref(t)('evaluation.modal.titles.add_analysis').toUpperCase(),
      message: _unref(t)('evaluation.modal.messages.add_analysis'),
      onCancel: _cache[10] || (_cache[10] = ($event: any) => (addCancel())),
      onConfirm: _cache[11] || (_cache[11] = ($event: any) => (addConfirm()))
    }, null, 8, ["show", "title", "message"]),
    _createVNode(_unref(baseConfirm), {
      show: showCopyConfirm.value,
      "max-width": "400px",
      title: 
        _unref(t)('common.labels.copy', {
          concat: _unref(t)('common.labels.analyse'),
        }).toUpperCase()
      ,
      message: 
        _unref(t)('common.texts.copy_message', {
          concat: `<strong>${selected.value?.code}</strong>`,
        })
      ,
      onCancel: _cache[12] || (_cache[12] = ($event: any) => (copyCancel())),
      onConfirm: _cache[13] || (_cache[13] = ($event: any) => (copyConfirm()))
    }, null, 8, ["show", "title", "message"]),
    _createVNode(_unref(baseConfirm), {
      show: showDelConfirm.value,
      "max-width": "400px",
      title: 
        _unref(t)('common.labels.remove', {
          concat: _unref(t)('common.labels.analyse'),
        }).toUpperCase()
      ,
      message: 
        _unref(t)('common.texts.remove_message', {
          concat: _unref(t)('common.labels.analyse'),
        })
      ,
      onCancel: _cache[14] || (_cache[14] = ($event: any) => (delCancel())),
      onConfirm: _cache[15] || (_cache[15] = ($event: any) => (delConfirm()))
    }, null, 8, ["show", "title", "message"])
  ], 2))
}
}

})